import {
  AdditionalQuestionResponseType,
  AnswerType,
  QuestionType,
} from '@hhgtech/hhg-components/types'

import { PhoneVerifyType } from 'state/types'

import { Article, ArticleApi } from './Article'
import { HealthToolInfoApiBmi } from './BMI'
import { CategoryApi } from './Category'
import { ReviewedUserApi } from './Reviewed'
import { ContentSponsor } from './Sponsor'

export type HealthTool = {
  id: number
  title: string
  subtitle: string
  icon: string
  link: string
  isSubot: boolean
  template: string
  isMobileAppTool: boolean
}
export type HealthToolApi = {
  id: number
  title: string
  icon: string
  link: string
  subtitle: string
  is_subot: boolean
  template: string
  is_mobile_app_tool: boolean
}
export type EmbeddedCommunityType = {
  title?: string
  description?: string
  community: number
}

export type HealthToolByCategory = {
  termId: number
  name: string
  healthTools: HealthTool[]
}

export type CategoryHealthToolApi = {
  term_id: number
  name: string
  health_tools: HealthToolApi[]
}

export type HealthToolInfoApi = {
  activity_level: string
  age: number
  gender: number
  height: number | null
  name: string

  bmis: HealthToolInfoApiBmi[] | null

  bmrs: HealthToolInfoApiBmr[] | null

  ovulation: HealthToolInfoApiOvulation[] | null

  pregnancies: HealthToolInfoApiPwg[] | null

  due_dates: HealthToolInfoApiDueDate[] | null
  heart_rates: unknown | null
}
export type HealthToolType =
  | 'bmi'
  | 'bmr'
  | 'ovulation'
  | 'due-date'
  | 'pregnancy-weight-gain'
  | 'heart-rate'
  | 'target-heart-rate'
  | 'vaccination-tool'
  | 'baby-poop-checking'
  | 'child-growth-chart'

export type HealthToolInfoApiBmr = {
  active_level: number
  age: number
  created_at: string
  gender: number
  height: number
  id: string
  kcal: number
  kcal_level: number
  objective: number
  updated_at: string
  weight: number
}

export type HealthToolInfoApiPwg = {
  bmi: number
  check_for: 'self' | 'other'
  created_at: string
  height: number
  id: string
  is_having_twin: boolean
  pregnancy_week: number
  suggest_weight_max: number
  suggest_weight_min: number
  updated_at: string
  weight: number
  weight_assessment: 'underweight' | 'healthy' | 'overweight'
  weight_before_pregnancy: number
}

export type HealthToolInfoApiOvulation = {
  created_at: string
  cycle_length_days: number
  fertile_from_date: string
  fertile_to_date: string
  first_day: string
  id: string
  menstrual_from_date: string
  menstrual_to_date: string
  objective: HealthWomanObjective
  ovulation_date: string
  period_duration_days: number
  updated_at: string
}

export type HealthWomanObjective = 2 | 3 | 4

export type HealthDueDateMethod = 1 | 2 | 3

export type HealthToolInfoApiDueDate = {
  id: string
  created_at: string
  updated_at: string
  account_id: number
  method: HealthDueDateMethod
  conception_date: string
  cycle_length_days: null | number
  days_transfer: null | number
  email: null | string
  first_day: null | string
  ivf_transfer_date: null | string
}

export type PWGCondition = 'underweight' | 'healthy' | 'overweight'

export type PWGResultApi = {
  enable_ad?: boolean
  suggestion: string
  faqs: { title: string; description: string }[]
  disclaimer: string
  suggested_articles: ArticleApi[]
  suggested_articles_link: string
  explaination: string
  weight_by_week: { week: number; min: number; max: number }[]
  care: string | any[]
  embeb: {
    pregnacy_navigation: Array<CategoryApi>
  }
  status: PWGCondition
}

export type PWGResult = {
  enable_ad: boolean
  assessmentData: { [tab: string]: string }[]
  faqs: { title: string; description: string }[]
  disclaimer: string
  suggestedArticles: Article[]
  suggestedArticlesLink: string
  weightByWeek: { week: number; min: number; max: number }[]
  care: string | any[]
  pregnancyNavigation: Array<CategoryApi>
  status: PWGCondition
}
export type PwgResult =
  | 'Athlete'
  | 'Excellent'
  | 'Good'
  | 'Average'
  | 'Poor'
  | 'Normal'
  | 'Abnormal'

export type HealthtoolReviewer = {
  reviewer: string
  date: string
  link: string
  avatar: string
}

export type THRResultApi = {
  enable_ad?: boolean
  suggestion: string
  faqs: { title: string; description: string }[]
  disclaimer: string
  suggested_articles: ArticleApi[]
  suggested_articles_link: string
  explaination: string
  ideal_heart_rate: { percent: number; range: string }
  maximum_heart_rate: number
  resting_heart_rate: {
    point: number
    value: string
    chart: { level: number; markup: number }[]
  }
  care: string | any
  community?: EmbeddedCommunityType
}

export type THRResult = {
  assessmentData: { [tab: string]: string }[]
  faqs: { title: string; description: string }[]
  disclaimer: string
  suggestedArticles: Article[]
  suggestedArticlesLink: string
  idealHeartRate: { percent: number; range: string }
  maximumHeartRate: number
  restingHeartRate: {
    heartRate: number
    status: PwgResult
    chart: { level: number; markup: number }[]
  }
  care: string | any
  community: EmbeddedCommunityType | null
}
export type CommonHealthtoolInfoApi = {
  title: string
  description: string
  list_questions: Array<AdditionalQuestionFieldTypeApi>
  sponsor_banner: AdditionalInfoApi
  enable_ad?: boolean
} & CollectingDataFormApi
export type CommonHealthtoolInfo = {
  title: string
  description: string
  listQuestions: Array<AdditionalQuestionFieldType>
  additionalInfo: AdditionalInfo
} & CollectingDataForm
export type AdditionalInfoApi = {
  image: string
  further_result: {
    title: string
    Content: string
    link: {
      url: string
      title: string
    }
  }
}
export type AdditionalInfo = {
  title: string
  description: string
  image: string
  ctaUrl: string
  ctaTitle: string
}
export type ToolContentApi = {
  is_sponsored: boolean
  sponsor: ContentSponsor
  featured_image: string
  information: {
    title: string
    description: string
  }[]
  reviewed_by: ReviewedUserApi
  reviewed_date: string
  meta_tag: string
  disclaimer: string
  avatar: string
  is_leadgen: boolean
} & CommonHealthtoolInfoApi

export type ToolContent = {
  enable_ad: boolean
  is_sponsored: boolean
  sponsor: ContentSponsor | null
  featured_image: string
  information: {
    title: string
    description: string
  }[]
  reviewBy: HealthtoolReviewer
  metaTag: string
  disclaimer: string
  isLeadgen: boolean
} & CommonHealthtoolInfo

export type PwgInfoApi = {
  disclaimer: string
  featured_image: string
  info: {
    title: string
    description: string
  }[]
  reviewed_by: ReviewedUserApi
  reviewed_date: string
  meta_tag?: string
  pregnacy_navigation: Array<CategoryApi>
  is_leadgen?: boolean
} & CommonHealthtoolInfoApi

export type PwgInfo = {
  enable_ad: boolean
  disclaimer: string
  featuredImage: string
  info: {
    title: string
    description: string
  }[]
  reviewBy: HealthtoolReviewer
  reviewedBy: ReviewedUserApi
  reviewedDate: string
  metaTag?: string
  pregnancyNavigation: Array<CategoryApi>
  isLeadgen: boolean
} & CommonHealthtoolInfo

export type ThrInfoApi = {
  disclaimer: string
  featured_image: string
  info: {
    title: string
    description: string
  }[]
  reviewed_by: ReviewedUserApi
  reviewed_date: string
  meta_tag?: string
  is_leadgen: boolean
} & CommonHealthtoolInfoApi

export type ThrInfo = {
  enable_ad: boolean
  disclaimer: string
  featured_image: string
  info: {
    title: string
    description: string
  }[]
  reviewedBy: ReviewedUserApi
  reviewedDate: string
  metaTag?: string
  reviewBy: HealthtoolReviewer
  isLeadgen: boolean
} & CommonHealthtoolInfo

export type OvulationInfoApi = {
  is_sponsored: boolean
  sponsor: ContentSponsor
  disclaimer: string
  featured_image: string
  info: {
    title: string
    description: string
  }[]
  reviewed_date: string
  reviewed_by: ReviewedUserApi
  meta_tag?: string
  is_leadgen?: boolean
  community?: EmbeddedCommunityType
} & CommonHealthtoolInfoApi
export type OvulationInfo = {
  enable_ad: boolean
  is_sponsored: boolean
  sponsor: ContentSponsor
  disclaimer: string
  featuredImage: string
  info: {
    title: string
    description: string
  }[]
  reviewedBy: {
    id: number | string
    permalink: string
    name: string
    postTitle: string
  }
  reviewBy: HealthtoolReviewer
  reviewedDate: string
  metaTag?: string
  isLeadgen?: boolean
  community?: EmbeddedCommunityType
} & CommonHealthtoolInfo

export type OvulationSuggestedArticleApi = {
  suggested_articles: ArticleApi[]
  suggested_articles_link: string
}

export type OvulationSuggestedArticle = {
  suggestedArticles: Article[]
  suggestedArticlesLink: string
}

export type BmrObjective = 'lose-fat' | 'maintain-weight' | 'build-muscle'
export type BmrActiveLevel =
  | 'sedentary'
  | 'lightly-active'
  | 'moderately-active'
  | 'intensely-active'
  | 'extremely-active'

export type BmrInfo = {
  age: string
  height: string
  weight: string
  objective: BmrObjective
  activityLevel: BmrActiveLevel
  isCheckYourself: boolean
}
export type PregWeightInfo = {
  yourSelf: string
  week: string
  weightBefore: string
  height: string
  weight: string
  twins: string
}

export type targetHeartRateInfo = {
  yourSelf: string
  gender: string
  age: string
  rate: string
  intensity: string
}

export type DueDateInfo = {
  post_title: string
  post_content: string
  disclaimer: string
  featured_image: string
  informations: [
    {
      title: string
      description: string
    },
  ]
  faqs: {
    [key: string]: [
      {
        title: string
        content: string
      },
    ]
  }
  reviewed_by: {
    ID: number | string
    name: string
    post_title: string
    permalink: string
    slug?: string
    role?: string
    user_id: number
    display_name?: string
    displayName?: string
    profile_url?: string
    profileUrl?: string
    avatar: string
  }
  reviewed_date: string
}

export type OvulationContent = {
  featured_image: string
  information: {
    title: string
    description: string
  }[]
  reviewed_by?: ReviewedUserApi
  reviewed_date: string
}
export type BabyInfo = {
  age: string
  gender: string
  name: string
}
export type BabyVaccinationInfoApi = {
  disclaimer: string
  info: {
    title: string
    description: string
  }[]
  reviewed_by: ReviewedUserApi
  reviewed_date: string
  meta_tag?: string
}

export type BabyVaccinationInfo = {
  disclaimer: string
  info: {
    title: string
    description: string
  }[]
  reviewedBy: ReviewedUserApi
  reviewedDate: string
  metaTag?: string
}
export type BabyVaccinationRecordsApi = {
  id: string
  vaccine_id: string
  vaccine_name: string
  vaccine_antigen: string
  given_on: string
  dose_number: number
  total_doses: number
  status: boolean
  vaccine_optional: boolean
}

export type BabyVaccinationStatus =
  | 'vaccinated'
  | 'missed'
  | 'next'
  | 'upcoming'
  | 'passed'

export type BabyVaccinationDetailApi = {
  profile: {
    id: string
    gender: boolean
    name: string
    dob: string
    new: boolean
  }
  timelines: {
    id: string
    name: string
    short_name: string
    from_day: number
    to_day: number
    records: BabyVaccinationRecordsApi[]
  }[]
  notifications: {
    id: string
    name: string
    short_name: string
    records: BabyVaccinationRecordsApi[]
  }[]
}
export type BabyVaccinationRecord = {
  id: string
  vaccineId: string
  vaccineName: string
  vaccineAntigen: string
  givenOn: string
  doseNumber: number
  totalDoses: number
  status: BabyVaccinationStatus
  optional: boolean
}

export type BabyVaccinationDetail = {
  profile: {
    id: string
    gender: 'male' | 'female'
    name: string
    dob: string
    new: boolean
  }
  timelines: {
    id: string
    name: string
    shortName: string
    fromDay: number
    toDay: number
    records: BabyVaccinationRecord[]
  }[]
  notifications: {
    id: string
    name: string
    shortName: string
    records: BabyVaccinationRecord[]
  }[]
}

export type InfoBabyVaccineResultPageApi = {
  faqs: { [x: string]: string }[]
  suggested_articles: ArticleApi[]
  suggested_articles_link: string
}

export type InfoBabyVaccineResultPage = {
  faqs: { [x: string]: string }[]
  suggestedArticles: Article[]
  suggestedArticlesLink: string
}

export type BabyVaccinationMainTimelinesApi = {
  id: string
  name: string
  vaccines: {
    id: string
    name: string
    antigen: string
    total_doses: number
    optional: boolean
    records: BabyVaccinationRecordsApi[]
  }[]
  timelines: {
    id: string
    name: string
    short_name: string
  }[]
}
export type BabyVaccinationMainTimelines = {
  id: string
  name: string
  vaccines: {
    id: string
    name: string
    antigen: string
    totalDoses: number
    optional: boolean
    records: BabyVaccinationRecord[]
  }[]
  timelines: {
    id: string
    name: string
    shortName: string
  }[]
}
export type BabyVaccinationVaccineInfoApi = {
  id: string
  name: string
  antigen: string
  brand_img: string
  benefits: string
  link: string
  contraindications: string
  optional: boolean
  detail: string
  records: BabyVaccinationRecordsApi[]
  pregnacy_navigation: Array<CategoryApi>
}

export type BabyVaccinationVaccineInfo = {
  id: string
  name: string
  antigen: string
  brandImg: string
  benefits: string
  link: string
  contraindications: string
  optional: boolean
  detail: string
  records: BabyVaccinationRecord[]
  pregnancyNavigation: Array<CategoryApi>
}

export type BabyVaccinationBannerApi = {
  image: string
  read_more_url: string
}

export type BabyVaccinationBanner = {
  image: string
  readMoreUrl: string
}

export type BabyPoopPageApi = {
  title: string
  english_name: string
  description: string
  featured_image: string
  is_sponsored: boolean
  sponsor: {
    title: string
    link: string
    logo: string
    ID: number
  } | null
  sponsor_banner: {
    desktop_image: string
    mobile_image: string
    show_sponsor_banner: boolean
    video_block?: {
      title: string
      list_video: {
        title: string
        video_url: string
      }[]
    } | null
  } | null
  disclaimer: string | null
  information:
    | {
        title: string
        description: string
      }[]
    | null
  tool_guideline: {
    title: string
    description: string
  } | null
  care: unknown[]
  reviewed_by: {
    ID: number
    user_id: number
    former_role: string
    role: string
    slug: string
    display_name: string
    description: string
    profile_url: string
    avatar: string
    avatar_thumbnail: string
    turn_off_profile: boolean
    specialty: string
    organisation: string
    organisation_url: string
  } | null
  reviewed_date: string | null
  content: string
  result: {
    [poop_type: string]: {
      image: string
      color: 'green' | 'red' | 'yellow'
      text_input: string
      title_result: string
      description: string
      what_should_i_do: string
      faqs:
        | {
            title: string
            description: string
          }[]
        | false
        | null
      recommend_articles?: ArticleApi[] | null
    }
  }
  is_leadgen: boolean
  meta_tag: string
} & CommonHealthtoolInfoApi

export type BabyPoopPage = {
  enable_ad: boolean
  title: string
  englishName: string
  description: string
  featuredImage: string
  isSponsored: boolean
  sponsor: {
    title: string
    link: string
    logo: string
    ID: number
  } | null
  sponsorBanner: {
    desktopImage: string
    mobileImage: string
    showSponsorBanner: boolean
    videoBlock?: {
      title: string
      listVideo: {
        title: string
        videoUrl: string
      }[]
    } | null
  } | null
  disclaimer: string | null
  information:
    | {
        title: string
        description: string
      }[]
    | null
  toolGuideline: {
    title: string
    description: string
  } | null
  care: unknown[]
  reviewedBy: {
    ID: number
    userId: number
    formerRole: string
    role: string
    slug: string
    displayName: string
    description: string
    profileUrl: string
    avatar: string
    avatarThumbnail: string
    turnOffProfile: boolean
    specialty: string
    organisation: string
    organisationUrl: string
  } | null
  reviewedDate: string
  content: string
  result: {
    name: string
    image: string
    color: 'green' | 'red' | 'yellow'
    textInput: string
    titleResult: string
    description: string
    whatShouldIDo: string
    faqs: {
      title: string
      description: string
    }[]
    recommendArticles: Article[]
  }[]
  isLeadgen: boolean
  metaTag: string
} & CommonHealthtoolInfo

export enum CollectingDataFormType {
  PHONE_ONLY = 'phone-verification',
  LEADGEN_FORM = 'leadgen-form',
  NONE = 'none', // FE default value
}

export type CollectingDataFormApi = {
  collecting_types: CollectingDataFormType
  collecting_data: {
    campaigns: any
    banner: {
      desktop: string
      mobile: string
    }
  }
  phone_verification: PhoneVerifyType
  is_skippable: boolean
}

export type CollectingDataForm = {
  collectingTypes: CollectingDataFormType
  collectingData: {
    campaigns: any
    banner: {
      desktop: string
      mobile: string
    }
  }
  phoneVerification: PhoneVerifyType
  isSkippable: boolean
}

export type QuestionTypeApi = {
  id: number
  question: string
  tooltip_question: string
}

export type AdditionalQuestionFieldTypeApi = {
  question: QuestionTypeApi
  answers: Array<AnswerType>
  has_another: boolean
  select_multi: boolean
  placeholder: string
}

export type AdditionalQuestionFieldType = {
  question: QuestionType
  questionHint?: string
  answers: Array<AnswerType>
  hasAnother?: boolean
  isMulti?: boolean
  otherInputPlaceholder?: string
  additionalQuestions?: AdditionalQuestionResponseType[]
}
